'use client';
import React, { useEffect, useState } from 'react';
import { ShieldCheck, Star, Search, StarIcon, Lock, MessageSquare } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';

const topFeatures = [
  {
    id: 'feat-verified',
    icon: ShieldCheck,
    title: 'Verified Student Reviews',
    desc: 'Only authenticated university students can post reviews.',
  },
  {
    id: 'feat-ratings',
    icon: Star,
    title: 'Honest Professor Ratings',
    desc: 'Star ratings, difficulty scores, and written feedback from peers.',
  },
];

const steps = [
  { icon: Lock, label: 'Sign in with university account' },
  { icon: Search, label: 'Find your professor or program' },
  { icon: StarIcon, label: 'Rate and leave a review' },
];

export default function LoginBrandPanel() {
  const [reviewCount, setReviewCount] = useState<number | null>(null);

  useEffect(() => {
    const fetchCount = async () => {
      const supabase = createClient();
      const { count } = await supabase
        .from('professor_reviews')
        .select('*', { count: 'exact', head: true });
      if (count !== null) setReviewCount(count);
    };
    fetchCount();
  }, []);

  return (
    <div className="bg-gradient-to-br from-[#1E3A5F] to-[#2563EB] p-8 md:p-10 flex flex-col justify-between text-white">
      <div className="flex flex-col gap-8">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <AppLogo size={40} />
          <span className="font-extrabold text-2xl tracking-tight">
            Uni<span className="text-amber-400">Rate</span>
          </span>
        </div>

        {/* Headline */}
        <div>
          <h2 className="text-2xl font-bold mb-2 leading-tight">
            Your academic journey, guided by peers.
          </h2>
          <p className="text-blue-200 text-sm">
            Sign in with your institutional Microsoft account to access reviews, rate professors, and help fellow students make better choices.
          </p>
        </div>

        {/* Top 2 features */}
        <div className="flex flex-col gap-4">
          {topFeatures.map((feat) => (
            <div key={feat.id} className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-white/15 shrink-0 mt-0.5">
                <feat.icon size={15} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold">{feat.title}</p>
                <p className="text-xs text-blue-200">{feat.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div className="bg-white/10 rounded-xl p-4">
          <p className="text-xs font-semibold text-blue-200 uppercase tracking-wider mb-3">How it works</p>
          <div className="flex flex-col gap-3">
            {steps.map((step, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-amber-400 text-[#1E3A5F] flex items-center justify-center text-xs font-bold shrink-0">
                  {i + 1}
                </div>
                <div className="flex items-center gap-2">
                  <step.icon size={13} className="text-blue-200 shrink-0" />
                  <p className="text-sm text-white/90">{step.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Real review count */}
        <div className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
          <div className="p-2 rounded-lg bg-amber-400/20 shrink-0">
            <MessageSquare size={16} className="text-amber-400" />
          </div>
          <div>
            {reviewCount === null ? (
              <div className="h-4 w-24 bg-white/20 rounded animate-pulse mb-1" />
            ) : (
              <p className="text-sm font-semibold">
                {reviewCount.toLocaleString()} review{reviewCount !== 1 ? 's' : ''} posted
              </p>
            )}
            <p className="text-xs text-blue-200">Real feedback from verified KIU students</p>
          </div>
        </div>

        {/* Privacy badge */}
        <div className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
          <div className="p-2 rounded-lg bg-white/15 shrink-0">
            <Lock size={15} className="text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold">Your identity is protected</p>
            <p className="text-xs text-blue-200">Reviews are anonymous to other students. UniRate never shares your password.</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 pt-6 border-t border-white/20">
        <p className="text-xs text-blue-200">
          Restricted access · @kiu.edu.ge accounts only · Microsoft SSO required
        </p>
      </div>
    </div>
  );
}